import { AbstractControl, AbstractControlOptions, AsyncValidatorFn, FormControl, ValidatorFn } from '@angular/forms';
import { Observable } from 'rxjs';
import { FormEventOptions, ResetValue } from '.';

/**
 * Typed form control is an `AbstractControl` with strong typed properties and methods.
 * Can be created using `typedFormControl` function
 *
 * @example
 * const c = typedFormControl<string>(): TypedFormControl<string>;
 * c.valueChanges // Observable<string>
 * c.patchValue('s') // expects string
 * c.patchValue(1) //  COMPILE TIME! type error!
 */
export interface TypedFormControl<K = any> extends FormControl, AbstractControl {
  patchValue: (v: Partial<K>, options?: FormEventOptions)=> void;
  reset: (value?: ResetValue<K>, opts?: FormEventOptions)=> void;
  setValue: (v: K, options?: FormEventOptions)=> void;
  value: K;
  valueChanges: Observable<K>;
}

/**
 * A helper function to create a `TypedFormControl`.
 * It only calls the constructor of FormControl, but **strongly types** the result.
 *
 * @param v the value to initialize our `TypedFormControl`
 * with - same as in `new FormControl(v, validators, asyncValidators)`
 * @param validatorsOrOptions validators - same as in new
 * `FormControl(v, validators, asyncValidators)`
 * @param asyncValidators async validators - same as in
 * `new FormControl(v, validators, asyncValidators)`
 *
 * @example
 * const c = typedFormControl<string>(): TypedFormControl<string>;
 * c.valueChanges // Observable<string>
 * c.patchValue('s') // expects string
 * c.patchValue(1) //  COMPILE TIME! type error!
 */
export function typedFormControl<T = any>(
  v?: T | { disabled: boolean; value: T; },
  validatorsOrOptions?: ValidatorFn | ValidatorFn[] | AbstractControlOptions,
  asyncValidators?: AsyncValidatorFn | AsyncValidatorFn[]
): TypedFormControl<T> {
  return new FormControl(v, validatorsOrOptions, asyncValidators);
}
